import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, Trash2, ArrowLeft, Filter, Wallet, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }
});

export default function BonusesPenalties() {
  const navigate = useNavigate();
  const [bonusesPenalties, setBonusesPenalties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterUser, setFilterUser] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  
  const [formData, setFormData] = useState({
    user_id: '',
    user_name: '',
    user_role: 'driver',
    type: 'bonus',
    amount: '',
    reason: ''
  });

  useEffect(() => {
    fetchBonusesPenalties();
    fetchUsers();
  }, []);

  const fetchBonusesPenalties = async () => {
    try {
      const response = await axios.get(`${API}/api/bonuses-penalties`, getAuthHeaders());
      setBonusesPenalties(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bonuses/penalties:', error);
      toast.error('Ошибка загрузки данных');
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/users`, getAuthHeaders());
      // Get drivers
      const filteredUsers = response.data.filter(u => 
        u.is_driver || u.role === 'driver'
      );
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Ошибка загрузки пользователей');
    }
  };

  const handleCreate = async () => {
    if (!formData.user_id || !formData.amount || formData.amount <= 0 || !formData.reason) {
      toast.error('Заполните все обязательные поля и укажите сумму больше 0');
      return;
    }

    try {
      await axios.post(`${API}/api/bonuses-penalties`, formData, getAuthHeaders());
      toast.success(`${formData.type === 'bonus' ? 'Премия' : 'Вычет'} добавлен(а)`);
      setShowCreateDialog(false);
      setFormData({
        user_id: '',
        user_name: '',
        user_role: 'driver',
        type: 'bonus',
        amount: '',
        reason: ''
      });
      fetchBonusesPenalties();
    } catch (error) {
      console.error('Error creating bonus/penalty:', error);
      toast.error(`Ошибка при создании: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить эту запись?')) return;

    try {
      await axios.delete(`${API}/api/bonuses-penalties/${id}`, getAuthHeaders());
      toast.success('Запись удалена');
      fetchBonusesPenalties();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Ошибка при удалении');
    }
  };

  const handleUserSelect = (value) => {
    const selected = users.find(u => u.id === value);
    const userRole = 'driver';
    setFormData({
      ...formData,
      user_id: value,
      user_name: selected?.username || '',
      user_role: userRole
    });
  };

  const getFilteredData = () => {
    let filtered = bonusesPenalties;
    
    if (filterUser && filterUser !== 'all') {
      filtered = filtered.filter(item => 
        item.user_id === filterUser
      );
    }
    
    if (filterType && filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }

    if (filterRole && filterRole !== 'all') {
      filtered = filtered.filter(item => item.user_role === filterRole);
    }
    
    return filtered;
  };

  const getTotals = () => {
    const filtered = getFilteredData();
    const bonuses = filtered.filter(item => item.type === 'bonus').reduce((sum, item) => sum + item.amount, 0);
    const penalties = filtered.filter(item => item.type === 'penalty').reduce((sum, item) => sum + item.amount, 0);
    return { bonuses, penalties, total: bonuses - penalties };
  };

  const drivers = users.filter(u => u.is_driver || u.role === 'driver');
  const totals = getTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8">
      {/* Header */}
      <div className="shadow-xl bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/')}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border border-white/30 shadow-lg hover:shadow-xl transition-all"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Назад
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
                  <Wallet className="w-7 h-7" />
                  Премии и вычеты
                </h1>
                <p className="text-sm text-white/90 font-medium mt-1">
                  Управление бонусами и вычетами для водителей
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-white text-purple-600 hover:bg-white/90 font-semibold shadow-lg hover:shadow-xl transition-all"
              size="sm"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Добавить</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-100 border-green-200 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-semibold mb-1">Всего премий</p>
                <p className="text-4xl font-bold text-green-900">{totals.bonuses.toFixed(2)} €</p>
              </div>
              <div className="p-4 bg-green-500 rounded-2xl shadow-lg">
                <TrendingUp className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-red-50 to-rose-100 border-red-200 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 font-semibold mb-1">Всего вычетов</p>
                <p className="text-4xl font-bold text-red-900">{totals.penalties.toFixed(2)} €</p>
              </div>
              <div className="p-4 bg-red-500 rounded-2xl shadow-lg">
                <TrendingDown className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-semibold mb-1">Итого</p>
                <p className={`text-4xl font-bold ${totals.total >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                  {totals.total >= 0 ? '+' : ''}{totals.total.toFixed(2)} €
                </p>
              </div>
              <div className={`p-4 ${totals.total >= 0 ? 'bg-blue-500' : 'bg-red-500'} rounded-2xl shadow-lg`}>
                <Wallet className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-5 mb-6 bg-white/80 backdrop-blur-sm shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">Фильтры</h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Роль</Label>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Все" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="driver">Водители</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Сотрудник</Label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Все сотрудники" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все сотрудники</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[150px]">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Тип</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Все типы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все типы</SelectItem>
                  <SelectItem value="bonus">💰 Только премии</SelectItem>
                  <SelectItem value="penalty">📉 Только вычеты</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* List */}
        <div className="space-y-4">
          {getFilteredData().length === 0 ? (
            <Card className="p-12 text-center bg-gradient-to-br from-gray-50 to-gray-100">
              <div className="text-gray-400 mb-4">
                <Wallet className="w-16 h-16 mx-auto opacity-30" />
              </div>
              <p className="text-gray-500 text-lg font-medium">Нет данных для отображения</p>
              <p className="text-gray-400 text-sm mt-2">Добавьте первую премию или вычет</p>
            </Card>
          ) : (
            getFilteredData().map(item => (
              <Card key={item.id} className="p-6 hover:shadow-xl transition-all bg-white border-l-4" style={{
                borderLeftColor: item.type === 'bonus' ? '#10b981' : '#ef4444'
              }}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {item.type === 'bonus' ? (
                        <div className="p-3 bg-green-100 rounded-xl shadow-sm">
                          <TrendingUp className="w-6 h-6 text-green-600" strokeWidth={2.5} />
                        </div>
                      ) : (
                        <div className="p-3 bg-red-100 rounded-xl shadow-sm">
                          <TrendingDown className="w-6 h-6 text-red-600" strokeWidth={2.5} />
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {item.type === 'bonus' ? '💰 Премия' : '📉 Вычет'}
                        </h3>
                        <p className="text-sm text-gray-600 font-medium flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Водитель</span>
                          {item.user_name} • {new Date(item.date).toLocaleDateString('ru-RU')}
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-3 pl-1 text-base">{item.reason}</p>
                    
                    <p className="text-xs text-gray-500 pl-1">
                      <span className="font-medium">Добавлено:</span> {item.created_by_name}
                    </p>
                  </div>

                  <div className="flex items-center gap-4 sm:flex-col sm:items-end">
                    <p className={`text-3xl sm:text-4xl font-bold ${item.type === 'bonus' ? 'text-green-600' : 'text-red-600'}`}>
                      {item.type === 'bonus' ? '+' : '-'}{item.amount.toFixed(2)} €
                    </p>
                    
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                      className="hover:scale-105 transition-transform"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Plus className="w-6 h-6 text-purple-600" />
              Добавить премию/вычет
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-4">
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Сотрудник *</Label>
              <Select value={formData.user_id} onValueChange={handleUserSelect}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Выберите сотрудника" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-100">
                        Водители
                      </div>
                      {drivers.map(driver => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.username}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Тип *</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonus">💰 Премия</SelectItem>
                  <SelectItem value="penalty">📉 Вычет</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Сумма (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
                className="h-11 text-lg"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Причина *</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                placeholder="Укажите причину начисления..."
                rows={4}
                className="resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowCreateDialog(false)} 
                className="flex-1 h-11"
              >
                Отмена
              </Button>
              <Button 
                onClick={handleCreate} 
                className="flex-1 h-11 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold shadow-lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Создать
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
